import styled from '@emotion/styled';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useElasticSearch from '../../hooks/useElasticSearch';
import useSelectedObjectState, { compareSelectInput } from '../../hooks/useSelectedObjectState';
import { getNormalizedQueryStringParameter } from '../../utils/queryString';
import ExinMuiCircularProgress from '../styled/ExinMuiCircularProgress';
import ExinSelect, { ExinSelectOption } from '../styled/ExinSelect';
import { ExinCaptionText } from '../styled/ExinText';
import ExinVoucherButton from '../styled/ExinVoucherButton';
import PaperPlane from '../styled/PaperPlane';

export type Certification = {
  value: string;
  label: string;
  exillenceId: string;
  productGroupId: string;
  trainingNeeded: boolean;
  materialsCount: number;
};

/**
 * If there are any URL search parameters that this app normally uses, remove them and re-set the
 * window.location.search.
 */
function resetUrlSearchParameters() {
  const searchParams = new URLSearchParams(window.location.search);
  if (
    ['center', 'country', 'certification', 'language'].reduce((wasChanged, queryParam) => {
      if (searchParams.has(queryParam)) {
        searchParams.delete(queryParam);
        return true;
      }
      return wasChanged;
    }, false)
  ) {
    window.location.search = searchParams.toString();
  }
}

function transformResponse(garbage: unknown): {
  certifications: Array<Certification>;
  levels: Array<ExinSelectOption>;
  programs: Array<ExinSelectOption>;
} {
  const certifications = (garbage as any).hits.hits.map((hit: any) => ({
    // Cast numeric identifier to string so it plays nice with MUI form elements
    value: String(hit._source.post_id),
    label: hit._source.post_title,
    exillenceId: hit._source.meta?.exillence_id?.[0].value,
    trainingNeeded:
      hit._source.meta?.training_required?.every((meta: { boolean: boolean }) => meta.boolean) ||
      false,
    productGroupId: hit._source.program?.exillence_id,
    materialsCount: parseInt(hit._source.meta?.materials?.[0].raw, 10) || 0,
  }));
  const levels = (garbage as any).aggregations.level.buckets.map((bucket: any) => ({
    value: bucket.key[0],
    label: bucket.key[1],
  }));
  const programs = (garbage as any).aggregations.program.buckets.map((bucket: any) => ({
    value: bucket.key[0],
    label: bucket.key[1],
  }));
  return { certifications, levels, programs };
}

const StepCertificationWrapper = styled.div`
  position: relative;
`;
const StepCertificationPaperPlaneWrapper = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  /* By approximation, the nose of the paper airplane should point at the bottom left corner of the certifications drop-down */
  transform: translate(-104%, 67%);
`;

function getQuery(
  selectedLevelFilter: ExinSelectOption | null = null,
  selectedProgramFilter: ExinSelectOption | null = null
) {
  const elasticSeachQueryMust: any = [
    {
      term: {
        'post_type.raw': 'ex_certification',
      },
    },
    {
      bool: {
        should: [
          {
            term: {
              product_type: 'career_path',
            },
          },
          {
            bool: {
              must: [
                {
                  nested: {
                    path: 'versions',
                    query: {
                      bool: {
                        should: [
                          {
                            bool: {
                              must: [
                                {
                                  range: {
                                    'versions.start_date': {
                                      lte: 'now',
                                    },
                                  },
                                },
                                {
                                  range: {
                                    'versions.end_date': {
                                      gte: 'now',
                                    },
                                  },
                                },
                              ],
                            },
                          },
                          {
                            bool: {
                              must: [
                                {
                                  range: {
                                    'versions.start_date': {
                                      lte: 'now',
                                    },
                                  },
                                },
                              ],
                              must_not: [
                                {
                                  exists: {
                                    field: 'versions.end_date',
                                  },
                                },
                              ],
                            },
                          },
                        ],
                      },
                    },
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ];
  if (selectedProgramFilter && selectedProgramFilter.value !== null) {
    elasticSeachQueryMust.push({
      term: {
        'program.exillence_id': selectedProgramFilter.value,
      },
    });
  }
  if (selectedLevelFilter && selectedLevelFilter.value !== null) {
    elasticSeachQueryMust.push({
      term: {
        'terms.ex_level.term_id': selectedLevelFilter.value,
      },
    });
  }
  return {
    query: {
      bool: {
        must: elasticSeachQueryMust,
      },
    },
    aggs: {
      level: {
        multi_terms: {
          size: 10000,
          terms: [
            {
              field: 'terms.ex_level.term_id',
            },
            {
              field: 'terms.ex_level.name.raw',
            },
          ],
        },
      },
      program: {
        multi_terms: {
          size: 10000,
          terms: [
            {
              field: 'program.exillence_id',
            },
            {
              field: 'program.title',
            },
          ],
        },
      },
    },
    size: 10000,
    _source: [
      'post_id',
      'post_title',
      'meta.training_required.boolean',
      'meta.materials.raw',
      'meta.exillence_id.value',
      'program.exillence_id',
    ],
  };
}

const alphabeticalSort = (a: { label: string }, b: { label: string }) => {
  const labelA = a.label.toUpperCase();
  const labelB = b.label.toUpperCase();
  if (labelA < labelB) {
    return -1;
  }
  if (labelA > labelB) {
    return 1;
  }
  return 0;
};

const StepCertification: FunctionComponent<{
  onChange(certification: Certification | null): void;
}> = ({ onChange }) => {
  const { i18n } = useTranslation();

  const [selectedProgramFilter, setProgramFilter] = useState();
  const [selectedLevelFilter, setLevelFilter] = useState();

  const { isLoading, response } = useElasticSearch(
    getQuery(selectedLevelFilter, selectedProgramFilter),
    transformResponse
  );

  const { levels: levelsMemoized, programs: programsMemoized } = useMemo(() => {
    const returnValue = response?.programs.length
      ? {
          programs: [
            {
              value: null,
              label: i18n.t('labelAnyProgram'),
            },
            ...response.programs.sort(alphabeticalSort),
          ],
          levels: [
            {
              value: null,
              label: i18n.t('labelAnyLevel'),
            },
            ...response.levels.sort(alphabeticalSort),
          ],
        }
      : { levelsMemoized: [], programsMemoized: [] };
    return returnValue;
  }, [i18n, response?.levels, response?.programs]);

  const [selectedCertification, setSelectedCertificationValue] = useSelectedObjectState(
    response?.certifications || null,
    getNormalizedQueryStringParameter('certification'),
    { onChange, comparator: compareSelectInput }
  );

  const onChangeWrapper = useCallback(
    (...args: Parameters<typeof setSelectedCertificationValue>) => {
      setSelectedCertificationValue(...args);
      resetUrlSearchParameters();
    },
    [setSelectedCertificationValue]
  );

  return (
    <StepCertificationWrapper>
      <StepCertificationPaperPlaneWrapper>
        <PaperPlane />
      </StepCertificationPaperPlaneWrapper>
      {!isLoading && (
        <>
          <ExinCaptionText>
            <Trans i18nKey="placeholderSelectLevelOrProgram">Specify level and/or program</Trans>
          </ExinCaptionText>
          <ExinSelect
            options={levelsMemoized}
            value={selectedLevelFilter}
            // @ts-ignore
            // Something in react-select#Select demands onChange data to be `unknown`
            // TypeScript will complain, because our own data is in fact _not_ unknown.
            onChange={setLevelFilter}
            placeholder={<Trans i18nKey="placeholderSelectLevel">Select level (optional)</Trans>}
            spaceBelow
          />
          <ExinSelect
            options={programsMemoized}
            value={selectedProgramFilter}
            // @ts-ignore
            // Something in react-select#Select demands onChange data to be `unknown`
            // TypeScript will complain, because our own data is in fact _not_ unknown.
            onChange={setProgramFilter}
            placeholder={
              <Trans i18nKey="placeholderSelectProgram">Select program (optional)</Trans>
            }
          />
        </>
      )}
      <ExinCaptionText>
        <Trans i18nKey="placeholderSelectCertification">Select certification</Trans>
      </ExinCaptionText>
      {isLoading ? (
        <ExinMuiCircularProgress />
      ) : (
        <ExinSelect
          isDisabled={!response?.certifications?.length}
          options={response?.certifications.sort(alphabeticalSort) || []}
          value={selectedCertification}
          // @ts-ignore
          // Something in react-select#Select demands onChange data to be `unknown`
          // TypeScript will complain, because our own data is in fact _not_ unknown.
          onChange={onChangeWrapper}
          placeholder={
            !response?.certifications?.length ? (
              <Trans i18nKey="messageNoCertifications">No certifications found</Trans>
            ) : (
              <Trans i18nKey="placeholderSelect">Select</Trans>
            )
          }
        />
      )}

      <p style={{ margin: '16px 0 0' }}>
        <ExinVoucherButton />
      </p>
    </StepCertificationWrapper>
  );
};

export default StepCertification;
